/**
 * usePaymentMethodCRUD Hook
 *
 * 결제수단 CRUD 작업을 위한 React Hook
 *
 * 책임:
 * - 결제수단 데이터 로드
 * - 결제수단 생성/수정/삭제
 * - 로딩/에러 상태 관리
 */

import { useState } from 'react'
import type { PaymentMethod } from '../types/PaymentMethod'
import { paymentMethodRepository } from '../repositories'

/**
 * usePaymentMethodCRUD Return Type
 */
export interface UsePaymentMethodCRUD {
  /** 결제수단 목록 */
  data: PaymentMethod[]
  /** 로딩 상태 */
  loading: boolean
  /** 에러 상태 */
  error: Error | null
  /** 전체 결제수단 로드 */
  loadAll: () => Promise<void>
  /** 새 결제수단 생성 */
  create: (input: Omit<PaymentMethod, 'id'>) => Promise<PaymentMethod>
  /** 결제수단 수정 */
  update: (id: string, input: Partial<PaymentMethod>) => Promise<PaymentMethod>
  /** 결제수단 삭제 */
  remove: (id: string) => Promise<void>
}

/**
 * 결제수단 CRUD Hook
 *
 * @returns CRUD 작업 메서드 및 상태
 *
 * @example
 * ```tsx
 * function PaymentMethodsPage() {
 *   const { data, loading, error, loadAll, create, update, remove } = usePaymentMethodCRUD()
 *
 *   useEffect(() => {
 *     loadAll()
 *   }, [])
 *
 *   const handleCreate = async () => {
 *     await create({
 *       name: '신용카드',
 *       type: 'credit',
 *       ownerId: 'user1',
 *       isActive: true
 *     })
 *     await loadAll()
 *   }
 * }
 * ```
 */
export function usePaymentMethodCRUD(): UsePaymentMethodCRUD {
  const [data, setData] = useState<PaymentMethod[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * 전체 결제수단 로드
   */
  const loadAll = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await paymentMethodRepository.findAll()
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * 새 결제수단 생성
   */
  const create = async (
    input: Omit<PaymentMethod, 'id'>
  ): Promise<PaymentMethod> => {
    try {
      setLoading(true)
      setError(null)
      const created = await paymentMethodRepository.create(input)
      return created
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * 결제수단 수정
   */
  const update = async (
    id: string,
    input: Partial<PaymentMethod>
  ): Promise<PaymentMethod> => {
    try {
      setLoading(true)
      setError(null)
      const updated = await paymentMethodRepository.update(id, input)
      return updated
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  /**
   * 결제수단 삭제
   */
  const remove = async (id: string): Promise<void> => {
    try {
      setLoading(true)
      setError(null)
      await paymentMethodRepository.delete(id)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('알 수 없는 오류'))
      throw err
    } finally {
      setLoading(false)
    }
  }

  return {
    data,
    loading,
    error,
    loadAll,
    create,
    update,
    remove,
  }
}
